import { createContext, useContext, useState, useCallback, useMemo } from "react";
import { api } from "../lib/api";

const DataContext = createContext(null);
export const useData = () => useContext(DataContext);

const DEFAULT_BUSINESS_PROFILE = {
  name: "", udyam: "", address: "", email: "", phone: "",
  bankAccountName: "", bankAccountNumber: "", bankName: "", ifsc: "",
};

export function DataProvider({ children }) {
  const [entries, setEntries] = useState([]);
  const [clients, setClients] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [businessProfile, setBusinessProfile] = useState(DEFAULT_BUSINESS_PROFILE);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    const res = await api.get("/data");
    const d = res.data;
    setEntries(d.entries || []);
    setClients(d.clients || []);
    setPipeline(d.pipeline || []);
    setInvoices(d.invoices || []);
    setBusinessProfile({ ...DEFAULT_BUSINESS_PROFILE, ...(d.businessProfile || {}) });
    setLoaded(true);
  }, []);

  const saveEntries = useCallback(async (next) => { setEntries(next); await api.put("/entries", { items: next }); }, []);
  const saveClients = useCallback(async (next) => { setClients(next); await api.put("/clients", { items: next }); }, []);
  const savePipeline = useCallback(async (next) => { setPipeline(next); await api.put("/pipeline", { items: next }); }, []);
  const saveInvoices = useCallback(async (next) => { setInvoices(next); await api.put("/invoices", { items: next }); }, []);
  const saveBusinessProfile = useCallback(async (next) => { setBusinessProfile(next); await api.put("/business-profile", next); }, []);

  // Bulk replace (used by Excel import)
  const replaceAll = useCallback(async (data) => {
    await Promise.all([
      api.put("/entries", { items: data.entries || [] }),
      api.put("/clients", { items: data.clients || [] }),
      api.put("/pipeline", { items: data.pipeline || [] }),
      api.put("/invoices", { items: data.invoices || [] }),
      api.put("/business-profile", { ...DEFAULT_BUSINESS_PROFILE, ...(data.businessProfile || {}) }),
    ]);
    await load();
  }, [load]);

  const value = useMemo(() => ({
    entries, clients, pipeline, invoices, businessProfile, loaded,
    load, saveEntries, saveClients, savePipeline, saveInvoices, saveBusinessProfile, replaceAll,
  }), [entries, clients, pipeline, invoices, businessProfile, loaded, load, saveEntries, saveClients, savePipeline, saveInvoices, saveBusinessProfile, replaceAll]);

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}
